import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval, Observable, Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { Logger } from '@app/@shared/logger.service';

const log = new Logger('ServiceWorkerUpdate');

/**
 * Watches for new application versions deployed via the Angular service worker.
 * When a new version is ready, subscribers are notified so a banner/countdown can be shown
 * before the page is reloaded onto the latest build.
 */
@Injectable({ providedIn: 'root' })
export class ServiceWorkerUpdateService {
  /** How often to poll the server for a new version. */
  private readonly checkInterval = 60 * 1000; // 1 minute

  private readonly versionReadySubject = new Subject<void>();

  /** Emits whenever a new application version has been downloaded and is ready to activate. */
  readonly versionReady$: Observable<void> = this.versionReadySubject.asObservable();

  constructor(private readonly swUpdate: SwUpdate, private readonly appRef: ApplicationRef) {}

  /**
   * Starts listening for version updates. Safe to call once on app start.
   * Does nothing when the service worker is disabled (e.g. in development).
   */
  init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Notify subscribers as soon as a new version is ready.
    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {
        log.info('New version available.');
        this.versionReadySubject.next();
      });

    // Recover from a broken/unrecoverable state by reloading immediately.
    this.swUpdate.unrecoverable.subscribe((event) => {
      log.error('Service worker in an unrecoverable state. Reloading...', event.reason);
      this.reloadApp();
    });

    // Periodically poll the server for new versions, but only once the app is stable.
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const everyInterval$ = interval(this.checkInterval);
    concat(appIsStable$, everyInterval$).subscribe(() => {
      this.swUpdate.checkForUpdate().catch((err) => log.warn('Failed to check for update', err));
    });
  }

  /** Reloads the page so the newest version is activated. */
  reloadApp(): void {
    document.location.reload();
  }
}
